import { observable, action } from 'mobx';
import { NavigationActions, StackActions } from 'react-navigation';

import { hidePicker } from '../components/shared/Picker';

import {
  getSkills,
  getTableExperience,
  getThings,
  getIslands,
  getConfig,
  getBuildings,
  getMentorBonuses,
} from '../lib/dbCalls';

import { InitDataType } from '../lib/types';
import { t, getFromStorage, setToStorage } from '../utils/appUtils';

import { VERSION } from '../lib/appConstants';

type MessageType = {
  type?: string;
  text: string | null;
  delay?: number;
  hideMsg?: boolean;
} | null;

type ConfigType = {
  initDataHash?: string;
  latestSupportedVersion?: string;
  adminToken?: string;
  version?: string;
};

const CONFIG_KEY = 'Config';
const INIT_DATA_KEY = 'InitData';

class App {
  config: ConfigType = {};

  initData!: InitDataType;

  @observable
  overlay: boolean = false;

  @observable
  isConnected: boolean | undefined | null = true;

  @observable
  loading: boolean = false;

  @observable
  chat: boolean = false;

  @observable
  chatState = { activeChat: 'inner', position: { x: 200, y: 50 }, scrollOffset: -1 };

  @observable
  smallLoading: boolean = false;

  @observable
  message: MessageType = null;

  @observable
  showSettingsModal: boolean = false;

  @observable
  showHelpModal: boolean = false;

  @observable
  currentNavs: { outer: string | null; inner: string | null } = {
    outer: null,
    inner: null,
  };

  navigationRefs: Record<string, any> = {};

  menuRef: any = null;

  @observable
  modalOpened = false;

  @action
  async fetchInitData() {
    this.config = await getConfig();
    const storageConfig = await getFromStorage(CONFIG_KEY);
    const storedInitData = await getFromStorage(INIT_DATA_KEY);

    if (
      !storageConfig ||
      storageConfig.initDataHash !== this.config.initDataHash ||
      storageConfig.version !== VERSION
    ) {
      this.initData = {
        skills: await getSkills(),
        tableExperience: await getTableExperience(),
        things: await getThings(),
        islands: await getIslands(),
        buildings: await getBuildings(),
        mentorBonuses: await getMentorBonuses(),
      };

      await setToStorage(INIT_DATA_KEY, this.initData);
      await setToStorage(CONFIG_KEY, {
        initDataHash: this.config.initDataHash,
        version: VERSION,
      });
    } else {
      this.initData = storedInitData;
    }
  }

  @action
  setNavigationRef(ref: any, type: string) {
    if (!ref) return;
    this.navigationRefs[type] = ref;
  }

  @action
  setMenuRef(ref: any) {
    this.menuRef = ref;
  }

  @action
  navigate(name: string, type: string = 'inner', navAction: string = 'navigate') {
    if (name === this.currentNavs[type as 'outer' | 'inner']) return;

    this.currentNavs[type as 'outer' | 'inner'] = name;

    const DEFAULT_INNER_NAV = 'Hero';

    let innerNav = DEFAULT_INNER_NAV;
    if (navAction === 'navigate') {
      this.navigationRefs[type]._navigation.navigate(name);
    } else if (navAction === 'reset') {
      const resetAction = StackActions.reset({
        index: 0,
        actions: [NavigationActions.navigate({ routeName: name })],
      });
      this.navigationRefs[type]._navigation.dispatch(resetAction);
    } else if (navAction === 'pop') {
      this.navigationRefs[type]._navigation.pop();
      innerNav = this.currentNavs.inner || innerNav;
    }

    if (type === 'outer' && name === 'Inner') {
      this.currentNavs.inner = innerNav;
    }

    hidePicker();
  }

  @action
  updateCurrentNav() {
    setTimeout(() => {
      const { state } = this.navigationRefs.inner._navigation;
      this.currentNavs.inner = state.routes[state.index].routeName;
    }, 0);
  }

  @action
  updateChatState(obj: {
    activeChat?: string;
    position?: { x: number; y: number };
    scrollOffset?: number;
  }) {
    this.chatState = {
      ...this.chatState,
      ...obj,
    };
  }

  @action
  toggleMenu() {
    return;
    // if (value) this.menuRef.open();
    // else this.menuRef.close();
  }

  @action
  toggleOverlay(value: boolean) {
    this.overlay = value;
  }

  @action
  toggleLoading(value: boolean) {
    this.toggleOverlay(value);
    this.loading = value;
  }

  @action
  toggleChat(value: boolean) {
    this.chat = value;
  }

  @action
  toggleSmallLoading(value: boolean) {
    this.smallLoading = value;
  }

  @action
  showMessage(message: MessageType) {
    this.message = { ...(message || ({} as any)), hideMsg: false };
  }

  @action
  hideMessage() {
    this.message = { ...(this.message || ({} as any)), hideMsg: true };
  }

  @action
  toggleConnected(value: boolean) {
    const firstTime = this.isConnected === null;
    if (value === this.isConnected) return;
    this.isConnected = value;
    if (firstTime && value) return;

    if (!value) {
      this.showMessage({
        type: 'error',
        text: t('noInternetConnection'),
      });
    } else {
      this.hideMessage();
    }
  }

  toggleModalOpened(value: boolean) {
    this.modalOpened = value;
  }

  clear() {
    this.currentNavs = {
      outer: null,
      inner: null,
    };
  }

  @action
  toggleSettingsModal(value: boolean = !this.showSettingsModal) {
    this.showSettingsModal = value;
  }

  @action
  toggleHelpModal(value: boolean = !this.showHelpModal) {
    this.showHelpModal = value;
  }
}

export default new App();
